/**
 * 网络状态、离线失败识别、离线重试队列。
 *
 * 三条来自生产事故的规则：
 *
 * 1. **离线时 `fetch` 抛的是 `TypeError`，不是 HTTP 错误**。所以那套"401 就跳登录页"的
 *    拦截器根本不会触发。如果某个页面依赖"拉不到数据就跳走"，断网时它会**永久**停在空白屏。
 *    判据：把网断掉，逐个页面点一遍，看有没有页面卡在骨架屏。
 *
 * 2. **不要指望 Background Sync**。iOS 完全没有，Chromium 上调度时机也由浏览器决定。
 *    自己维护一个持久化重试队列，在"回到线上"和"应用启动"这两个时机冲刷——这两个时机
 *    在所有平台上都存在。
 *
 * 3. **失败必须有可见的落点**。发送失败要有可重发的失败态；上传失败至少要一句提示；
 *    批量操作要逐项 try/catch，一项失败不能把后面几项一起带走；**截断/丢弃必须出声**。
 */
import { createSafeStorage } from './storage.js';
/**
 * 这个错误是不是"网络没通"，而不是"服务端返回了错误"。
 *
 * fetch 只在网络层失败时 reject（TypeError）；HTTP 4xx/5xx 是 resolve 的。
 * AbortError 单独排除——那是我们自己取消的，不该走重试。
 */
export function isNetworkError(e) {
    if (e instanceof DOMException && e.name === 'AbortError')
        return false;
    if (!(e instanceof TypeError))
        return false;
    // TypeError 也可能是代码写错（比如 undefined.foo）。只认 fetch 家族的报错文案，
    // 宁可漏判成"代码错误"（会被日志抓到），也不要把真的 bug 当成断网吞掉。
    const msg = e.message.toLowerCase();
    return (msg.includes('fetch') ||
        msg.includes('network') ||
        msg.includes('load failed') || // Safari
        msg.includes('connection') ||
        msg.includes('offline'));
}
/**
 * ⚠️ `navigator.onLine === true` 只说明"有网卡连着"，不说明"能连上你的服务器"。
 * 它可以用来乐观地触发重试，不能用来判断请求会不会成功。
 */
export function createOnlineState() {
    const listeners = new Set();
    const emit = () => {
        const v = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
        for (const l of listeners)
            l(v);
    };
    const hasWindow = typeof window !== 'undefined';
    if (hasWindow) {
        window.addEventListener('online', emit);
        window.addEventListener('offline', emit);
    }
    return {
        online: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        dispose() {
            listeners.clear();
            if (hasWindow) {
                window.removeEventListener('online', emit);
                window.removeEventListener('offline', emit);
            }
        },
    };
}
let seq = 0;
function newId() {
    seq += 1;
    const rand = Math.random().toString(36).slice(2, 10);
    return `${Date.now().toString(36)}-${seq.toString(36)}-${rand}`;
}
export function createRetryQueue(options) {
    const storage = options.storage ?? createSafeStorage();
    const maxAttempts = options.maxAttempts ?? 8;
    const KEY = options.namespace.key('queue');
    let flushing = false;
    const load = () => storage.getJSON(KEY, []);
    const save = (tasks) => {
        storage.setJSON(KEY, tasks);
        options.onChange?.(tasks.length);
    };
    const queue = {
        enqueue(kind, payload, id) {
            const tasks = load();
            const taskId = id ?? newId();
            const existing = tasks.find((t) => t.id === taskId);
            if (existing)
                return existing;
            const task = { id: taskId, kind, payload, attempts: 0, createdAt: Date.now() };
            tasks.push(task);
            save(tasks);
            return task;
        },
        async flush() {
            // 并发冲刷会把同一个任务跑两遍。一次只允许一个。
            if (flushing)
                return { done: 0, failed: 0 };
            flushing = true;
            let done = 0;
            let failed = 0;
            try {
                // 每一轮重新读，因为 run() 里可能又入队了新任务
                for (;;) {
                    const tasks = load();
                    const task = tasks[0];
                    if (!task)
                        break;
                    try {
                        await options.run(task);
                        save(load().filter((t) => t.id !== task.id));
                        done += 1;
                    }
                    catch (e) {
                        failed += 1;
                        const next = load();
                        const idx = next.findIndex((t) => t.id === task.id);
                        if (idx < 0)
                            break;
                        const current = next[idx];
                        current.attempts += 1;
                        if (current.attempts >= maxAttempts) {
                            next.splice(idx, 1);
                            save(next);
                            // 放弃了就必须说出去。静默丢弃是最差的失败方式。
                            options.onGiveUp?.(current, e);
                        }
                        else {
                            save(next);
                        }
                        // 网络没通就别继续磨了，等下一次 online 事件
                        if (isNetworkError(e))
                            break;
                        // 非网络错误：把这条挪到队尾，别让它卡住后面的
                        if (current.attempts < maxAttempts) {
                            const rest = load();
                            const i = rest.findIndex((t) => t.id === task.id);
                            if (i >= 0) {
                                const [moved] = rest.splice(i, 1);
                                if (moved)
                                    rest.push(moved);
                                save(rest);
                            }
                        }
                    }
                }
            }
            finally {
                flushing = false;
            }
            return { done, failed };
        },
        pending: load,
        remove(id) {
            save(load().filter((t) => t.id !== id));
        },
        clear() {
            save([]);
        },
        start() {
            const online = createOnlineState();
            const unsub = online.subscribe((isOnline) => {
                if (isOnline)
                    void queue.flush();
            });
            const onVisible = () => {
                if (typeof document !== 'undefined' && document.visibilityState === 'visible')
                    void queue.flush();
            };
            if (typeof document !== 'undefined')
                document.addEventListener('visibilitychange', onVisible);
            // 启动时立刻冲一次：这是 iOS 上唯一可靠的时机
            void queue.flush();
            return () => {
                unsub();
                // unsub() 只是把回调从 Set 里删掉，window 上那对 online/offline 监听要靠 dispose 摘
                online.dispose();
                if (typeof document !== 'undefined')
                    document.removeEventListener('visibilitychange', onVisible);
            };
        },
    };
    return queue;
}
/**
 * 串行执行，逐项 try/catch。
 *
 * 为什么串行：多张图片并发上传 → 谁先传完谁先发 → 对方看到的顺序和你选的不一样，
 * 而图片顺序常常本身就是信息。慢一点，但对。
 *
 * 为什么返回 report 而不是抛：一项失败不能把后面几项一起带走，而且**每一项的失败都需要
 * 一个可见的落点**。返回值强迫调用方面对失败列表。
 */
export async function runSequential(items, fn, options = {}) {
    const limit = options.limit ?? items.length;
    const take = items.slice(0, limit);
    const report = { ok: [], failed: [], skipped: items.slice(limit) };
    for (let i = 0; i < take.length; i += 1) {
        const item = take[i];
        try {
            report.ok.push({ item, result: await fn(item, i) });
        }
        catch (error) {
            report.failed.push({ item, error });
        }
    }
    return report;
}
//# sourceMappingURL=net.js.map